import React, { Suspense, useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { listWorldObjects } from '../services/worldObjects';
import AnimatedResident from './AnimatedResident';
import { FACING_STREET } from '../worldCompass';

// Bouncers — every door in the city that has somebody on it.
//
// Khari, 2026-08-03: "It would be better add a 'bouncer' item in the admin. This
// way, i can add a gate to any building, using the bouncer as the repeated
// method. The current concert guard panel could be adjusted/enhanced to fit this
// more global use."
//
// This replaces two single-fixture components (ConcertGuard, StudioGuard) that
// were the same forty lines with a different constant in them — which is how you
// end up placing the second one by copying the first one's stale defaults and
// inverting the sign, as I did.
//
// A bouncer is a PLACEMENT now: kind 'guard', zone 'street', `ref` naming the
// venue he stands at. Adding one to a building is a row, so a new gate is a
// placement rather than a component. The RULE he enforces is not here and never
// was — it lives in world_gates keyed by the same ref, so placing a body and
// deciding who gets past are two independent jobs, and either can exist without
// the other. A door can be gated with nobody on it, and a bouncer can stand at
// an open door as scenery.
//
// Positions are OFFSETS from the venue anchor (see worldCompass): the anchors
// come from the city model at runtime, so an absolute position drifts the moment
// the model does — and programmatic plotting needs a convention, not coordinates.
const SEEDS = [
  // Until a row is saved, these keep a body on the door. The concert hall's real
  // placement lives in its saved row and overrides this the moment it loads.
  { ref: 'concert-hall', pos: [-0.75, 0.1, 3.5], rotY: FACING_STREET, size: 0.68 },
  { ref: 'studio', pos: [-0.75, 0.1, 5], rotY: FACING_STREET, size: 0.68 },
];

export default function Bouncers() {
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const editingRef = useWorldStore((s) => s.guardRef);
  const tune = useWorldStore((s) => s.guardTune);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let dead = false;
    listWorldObjects('street', 'guard').then((r) => { if (!dead) setRows(r || []); });
    return () => { dead = true; };
  }, []);

  if (!venueAnchors) return null;

  // Saved rows win over seeds, by ref. A venue with neither gets nobody, which
  // is the correct reading of "no bouncer placed here".
  const byRef = new Map();
  for (const s of SEEDS) byRef.set(s.ref, s);
  for (const r of rows || []) {
    if (r?.ref) byRef.set(r.ref, { ref: r.ref, pos: r.pos, rotY: r.rotY, size: r.size });
  }

  return (
    <>
      {[...byRef.values()].map((b) => {
        const anchor = venueAnchors[b.ref];
        if (!anchor) return null;
        // The one being edited follows the panel live, so nudging a body moves
        // the body rather than a preview of it.
        const live = editingRef === b.ref && tune;
        const p = live ? [tune.x, tune.y, tune.z] : (b.pos || [0, 0, 0]);
        const rot = live ? tune.rot : (b.rotY || 0);
        const h = live ? tune.h : (b.size || 0.68);
        const [ax, , az] = anchor;
        return (
          <Suspense key={b.ref} fallback={null}>
            <AnimatedResident
              meshUrl="/models/security_guard.glb"
              clipUrls={[]}
              play="idle"
              height={h}
              position={[ax + (p[0] || 0), p[1] || 0, az + (p[2] || 0)]}
              rotationY={rot}
              // He clocks you on the approach — wider than a resident's 2.4,
              // because a doorman looks up before you reach him, and that look
              // is what makes a refusal land as a person rather than a panel.
              attentionRadius={4}
              midRadius={11}
            />
          </Suspense>
        );
      })}
    </>
  );
}
